/**
 * Route manifest for the REMAINING rent sub-pages and the ADMIN console (S2).
 *
 * Companion to `rent-core.routes.tsx`. That manifest ships the eight core rent
 * routes (dashboard, properties, tenants, agreements, payments); this one ships
 * the other thirteen rent sub-pages plus the five admin-console pages, so the
 * two compose into the full `/rent` subtree without either editing
 * `routes.tsx` — which stays generated from `PAGE_REGISTRY`.
 *
 * ── Rent paths are RELATIVE to `/rent` ─────────────────────────────────────
 * `PAGE_REGISTRY` maps `matchPrefix: "/rent"` to one routed subtree, so every
 * rent route below is a child of it. None of them is a nav entry: the navbar
 * is the fixed 7-entry `NAV_PAGES` set and rent sub-pages are tabs/sub-routes
 * reached from inside the single `Rent` destination (the `RentSubNav` in
 * `RentLayout` is what links them).
 *
 * ── Admin paths are RELATIVE to `/admin`, and `/admin` is NOT in the registry
 * That is deliberate and matches Next.js exactly. `middleware.ts` checks
 * `pathname.startsWith("/admin")` BEFORE it consults the registry and returns
 * on the spot, so admin pages have never been matrix-gated — they are a hard
 * `role === "ADMIN"` check whose denial is `/no-access?page=admin`.
 * `route-guard.ts::isAdminOnlyPath` ports that branch verbatim, and it is
 * `RequireAuth` (which wraps every routed page) that runs it.
 *
 * ── SAFE-04 ────────────────────────────────────────────────────────────────
 * No page here becomes newly reachable or newly denied:
 *
 *   - rent sub-pages sit inside the `/rent` prefix, so they are gated by the
 *     same `resolvePermission("rent", "view")` check they are gated by today,
 *     and FastAPI still enforces `rentModuleAccess` on every call they make;
 *   - admin pages are wrapped in `AdminOnly` IN ADDITION to the router gate,
 *     so a mis-wiring that mounted them outside `RequireAuth` would still
 *     deny a non-ADMIN rather than silently expose five admin screens. The
 *     two gates return the same answer for every session — `AdminOnly` is
 *     redundant on purpose, never stricter.
 *   - `/admin/import-budgetbakers` additionally keeps the inline ADMIN check
 *     its Next.js server component had, with that page's own `/dashboard`
 *     destination.
 *
 * Two dev-only scratch pages under `/admin` are deliberately NOT ported and
 * NOT routed: `admin/design-mockup-d` and `admin/design-system`. They are
 * design sandboxes, not product. Dropping a route removes nothing a family
 * member could reach — both were ADMIN-only, and neither is linked from the
 * app — so SAFE-04 is unaffected.
 *
 * ── Wiring ─────────────────────────────────────────────────────────────────
 * Rent, either shape (mirrors `rent-core.routes.tsx`):
 *
 *   // (a) registry route is `{ path: "rent/*", element: … }` (today's shape)
 *   //     — compose with the core manifest under ONE shared layout:
 *   useRoutes([{ element: <RentLayout />,
 *                children: [...rentCoreRoutes, ...rentExtrasRoutes] }])
 *
 *   // (b) registry route becomes `{ path: "rent", children: […] }`
 *   children: [{ element: <RentLayout />,
 *                children: [...rentCoreRoutes, ...rentExtrasRoutes] }]
 *
 * Admin — add ONE route to the `RequireAuth`/`DashboardLayout` children in
 * `routes.tsx`:
 *
 *   { path: "admin/*", element: <AdminRoutes /> }
 *   // or: { path: "admin", children: adminRoutesGuarded }
 */
import { Suspense, type ReactNode } from "react";
import { Navigate, useLocation, useRoutes, type RouteObject } from "react-router-dom";

import { AdminOnly } from "@/components/admin/AdminOnly";
import { lazyNamed, RouteSkeleton } from "@/lib/prefetch";
import { DATA_TAB_REDIRECTS } from "@/pages/admin/dataTabRedirects";

function s(element: ReactNode): ReactNode {
  return <Suspense fallback={<RouteSkeleton />}>{element}</Suspense>;
}

// Rent sub-pages (S3: lazy, same reasoning as `rent-core.routes.tsx`)
const RentExpensesPage = lazyNamed(
  () => import("@/pages/rent/expenses/RentExpensesPage"),
  "RentExpensesPage",
);
const BrokersPage = lazyNamed(() => import("@/pages/rent/brokers/BrokersPage"), "BrokersPage");
const BrokerDetailPage = lazyNamed(
  () => import("@/pages/rent/brokers/BrokerDetailPage"),
  "BrokerDetailPage",
);
const TdsPage = lazyNamed(() => import("@/pages/rent/tds/TdsPage"), "TdsPage");
// `ReportsPage` (this one, `/rent/reports`) already dynamic-imports
// `analytics-pdf.tsx` (the 1.3 MB `@react-pdf/renderer` chunk) itself on
// demand — lazy-loading the page too keeps that deferral intact while also
// keeping the page's own (non-PDF) code out of the eager graph.
const ReportsPage = lazyNamed(() => import("@/pages/rent/reports/ReportsPage"), "ReportsPage");
const StatementsPage = lazyNamed(
  () => import("@/pages/rent/statements/StatementsPage"),
  "StatementsPage",
);
const StatementReviewPage = lazyNamed(
  () => import("@/pages/rent/statements/StatementReviewPage"),
  "StatementReviewPage",
);
const MaintenancePage = lazyNamed(
  () => import("@/pages/rent/maintenance/MaintenancePage"),
  "MaintenancePage",
);
const VacancyPage = lazyNamed(() => import("@/pages/rent/vacancy/VacancyPage"), "VacancyPage");
const LandlordPage = lazyNamed(() => import("@/pages/rent/landlord/LandlordPage"), "LandlordPage");
const ReminderLogPage = lazyNamed(
  () => import("@/pages/rent/reminders/ReminderLogPage"),
  "ReminderLogPage",
);
const ReminderSettingsPage = lazyNamed(
  () => import("@/pages/rent/settings/ReminderSettingsPage"),
  "ReminderSettingsPage",
);
const NoticeSettingsPage = lazyNamed(
  () => import("@/pages/rent/settings/NoticeSettingsPage"),
  "NoticeSettingsPage",
);

// Admin console — six family members can never reach any of this (USER role),
// so it was pure dead weight in their bundle before this split.
//
// Wave 6 (B5): the five leaf pages that used to be five sibling routes now
// live INSIDE three group hub pages (`AdminOverviewPage` / `AdminPeoplePage` /
// `AdminDataPage` / `AdminSystemPage`, in `pages/admin/`) — see that
// directory for why (owner: "admin panel consolidated is needed things are
// not organised in structure"). This manifest only needs the hub pages plus
// `Navigate` redirects for the old leaf URLs now.
const AdminOverviewPage = lazyNamed(
  () => import("@/pages/admin/AdminOverviewPage"),
  "AdminOverviewPage",
);
const AdminPeoplePage = lazyNamed(() => import("@/pages/admin/AdminPeoplePage"), "AdminPeoplePage");
const AdminSystemPage = lazyNamed(() => import("@/pages/admin/AdminSystemPage"), "AdminSystemPage");
// 2026-09-04: management is now ALSO indexed by navbar module, because the
// "Data" group had become a grab-bag spanning three unrelated modules while
// half the per-module admin (rent templates, tax structure) was not in the
// console at all. `pages/admin/modules.tsx` is the single index; these two
// pages just render it.
const AdminModulesPage = lazyNamed(
  () => import("@/pages/admin/AdminModulesPage"),
  "AdminModulesPage",
);
const AdminModulePage = lazyNamed(
  () => import("@/pages/admin/AdminModulePage"),
  "AdminModulePage",
);

/**
 * The thirteen rent leaf routes, WITHOUT the layout, so they can be
 * concatenated with `rentCoreRoutes` under one shared `RentLayout` parent
 * instead of each module mounting its own header and payment FAB.
 *
 * Paths are the Next.js URLs byte-for-byte (minus the leading `/rent/`), which
 * matters: these are linked from `RentSubNav`, bookmarked, and reached from
 * reminder emails.
 */
export const rentExtrasRoutes: RouteObject[] = [
  { path: "expenses", element: s(<RentExpensesPage />) },
  { path: "brokers", element: s(<BrokersPage />) },
  { path: "brokers/:id", element: s(<BrokerDetailPage />) },
  { path: "tds", element: s(<TdsPage />) },
  { path: "reports", element: s(<ReportsPage />) },
  { path: "statements", element: s(<StatementsPage />) },
  { path: "statements/:id", element: s(<StatementReviewPage />) },
  { path: "maintenance", element: s(<MaintenancePage />) },
  { path: "vacancy", element: s(<VacancyPage />) },
  { path: "landlord", element: s(<LandlordPage />) },
  { path: "reminders/log", element: s(<ReminderLogPage />) },
  { path: "settings/reminders", element: s(<ReminderSettingsPage />) },
  { path: "settings/notices", element: s(<NoticeSettingsPage />) },
];

/**
 * `/admin/data?tab=X` -> wherever X lives now.
 *
 * A plain `<Navigate>` cannot do this: the destination depends on the QUERY,
 * and the eight old tabs scattered to four different places (five to Records,
 * one each to Accounts, Budget and System). Anything unrecognised -- including
 * a bare `/admin/data` -- falls through to the module index, which is the
 * closest thing to what the page used to be.
 */
function DataTabRedirect() {
  const tab = new URLSearchParams(useLocation().search).get("tab") ?? "";
  return <Navigate to={DATA_TAB_REDIRECTS[tab] ?? "/admin/modules"} replace />;
}

/**
 * The Admin Console routes, relative to `/admin`, WITHOUT the guard —
 * exported for callers that mount their own. Prefer `adminRoutesGuarded`.
 *
 * Four real destinations (the status-dashboard landing view plus the three
 * groups), and one redirect per RETIRED leaf URL so every bookmark, every
 * `Link href="/admin/…"` still in the codebase (`TopBar.tsx`, `nav.ts`), and
 * anything a family member saved keeps working. `tests/admin-hub.test.tsx`
 * (Wave 6, B5) asserts every one of these seven old paths resolves.
 */
export const adminRoutes: RouteObject[] = [
  { index: true, element: s(<AdminOverviewPage />) },
  { path: "people", element: s(<AdminPeoplePage />) },
  { path: "modules", element: s(<AdminModulesPage />) },
  { path: "module/:key", element: s(<AdminModulePage />) },
  { path: "system", element: s(<AdminSystemPage />) },

  // The old grab-bag. Kept as a route rather than deleted so that every
  // `/admin/data?tab=…` bookmark, and the `Link`s still pointing at it,
  // resolve to the module that now owns that tab -- see `DataTabRedirect`.
  { path: "data", element: <DataTabRedirect /> },

  // --- retired leaf URLs -> redirect into the hub (nothing removed) --------
  { path: "users", element: <Navigate to="/admin/people?tab=users" replace /> },
  { path: "categories", element: <Navigate to="/admin/module/records?tab=categories" replace /> },
  {
    path: "access-requests",
    element: <Navigate to="/admin/people?tab=access-requests" replace />,
  },
  { path: "commitments", element: <Navigate to="/admin/module/budget?tab=commitments" replace /> },
  {
    path: "import-budgetbakers",
    element: <Navigate to="/admin/module/records?tab=import" replace />,
  },
  // V5-14 sub-item 4 — user-defined fields on records. Now `Data > Custom Fields`.
  { path: "custom-fields", element: <Navigate to="/admin/module/records?tab=custom-fields" replace /> },
  // V5-06 audit console — now `System > Audit Log` (was already a duplicate
  // of `/settings?tab=audit`; both now redirect to the one real screen).
  { path: "audit", element: <Navigate to="/admin/system?tab=audit" replace /> },
];

/** The admin routes behind their own `role === "ADMIN"` layout gate. */
export const adminRoutesGuarded: RouteObject[] = [
  { element: <AdminOnly />, children: adminRoutes },
];

/** Element form for a `rent/*` splat that composes with the core manifest. */
export function RentExtrasRoutes() {
  return useRoutes(rentExtrasRoutes);
}

/** Element form for an `admin/*` splat route inside `RequireAuth`. */
export function AdminRoutes() {
  return useRoutes(adminRoutesGuarded);
}

export default rentExtrasRoutes;
